import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import MDButton from "mdComponents/MDButton";
import Rating from "@mui/material/Rating";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import TextField from "@mui/material/TextField";

import locationsService from "../../services/locationsService";
import userRatingService from "../../services/userRatingService";
import locationSuspendService from "../../services/locationSuspendService";
import { formatDate } from "../../Utils";
import MDInput from "mdComponents/MDInput";
import { useUser } from "context/UserContext";
import NotificationAlert from "controls/NotificationAlert";
import { SUSPEND_MEMBER_TIME, SUSPEND_USER_REASONS } from "../../constants";
import LoadingSpinner from "controls/LoadingSpinner";

const LocationActivityDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useUser();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState<any>(null);

  const [userRating, setUserRating] = useState<number>(0);
  const [noteText, setNoteText] = useState<string>("");
  const [pictureLoaded, setPictureLoaded] = useState(false);

  const [showSnackBar, setShowSnackBar] = useState(false);
  const [snackbar, setSnackbar] = useState({
    color: "success",
    content: "Saved Successfully!",
    title: "Save Successful",
    icon: "check",
    dateTime: "just now",
  });

  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [suspendAction, setSuspendAction] = useState<"suspend" | "ban">("suspend");
  const [suspendLength, setSuspendLength] = useState("7 days");
  const [suspendReason, setSuspendReason] = useState<string>("");
  const [suspendErrors, setSuspendErrors] = useState<{ [key: string]: string }>({});
  const [savingSuspend, setSavingSuspend] = useState(false);

  const openSnackBar = () => setShowSnackBar(true);
  const closeSnackBar = () => setShowSnackBar(false);

  useEffect(() => {
    const loadActivity = async () => {
      setLoading(true);
      try {
        const { data, error } = await locationsService.fetchLocationActivityById(id);
        if (error || !data) {
          console.error(error);
          setActivity(null);
        } else {
          setActivity(data);
          setUserRating(data.UserRating?.[0]?.rating ?? 0);
          setNoteText(data.UserRating?.[0]?.note ?? "");
        }
      } finally {
        setLoading(false);
      }
    };
    loadActivity();
  }, [id]);

  const onRateUser = async (newVal: number) => {
    if (!activity) return;
    const savedRatingId = activity.UserRating?.[0]?.id ?? -1;
    if (savedRatingId !== -1) return;

    const result = await userRatingService.putUserRating({
      id: savedRatingId,
      hostId: activity.hostId,
      userId: activity.userId,
      ratedByUserId: user.id,
      workflowId: activity.id,
      rating: newVal,
      note: noteText,
    });
    if (result !== -1) {
      setUserRating(newVal);
      setSnackbar({
        color: "success",
        content: "User Rating saved successfully.",
        title: "Save Rating Successful",
        icon: "check",
        dateTime: "just now",
      });
      openSnackBar();
    } else {
      setSnackbar({
        color: "error",
        content: "There was an error saving the user rating. Please try again.",
        title: "Save Rating Failed",
        icon: "error",
        dateTime: "just now",
      });
      openSnackBar();
    }
  };

  const validateSuspendForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!suspendReason) newErrors.reason = "Reason is required";
    if (!noteText) newErrors.note = "A note is required";
    setSuspendErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveSuspend = async () => {
    if (!activity || !validateSuspendForm()) return;
    setSavingSuspend(true);

    const lengthInDays = SUSPEND_MEMBER_TIME.find((a) => a.name === suspendLength)?.id ?? 7;

    const payload = {
      userId: activity.userId,
      hostId: activity.hostId,
      workflowId: activity.id,
      isBan: suspendAction === "ban",
      lengthInDays,
      reason: suspendReason,
      notes: noteText,
    };

    const result = await locationSuspendService.insertUserLocationSuspend(payload as any);
    if (result === true) {
      setSnackbar({
        color: "success",
        content: `User has been ${suspendAction === "ban" ? "banned" : "suspended"}`,
        title: "Action Successful",
        icon: "check",
        dateTime: "just now",
      });
      openSnackBar();
      setSuspendModalOpen(false);
    } else {
      setSnackbar({
        color: "error",
        content: "Failed to save. Please try again.",
        title: "Action Failed",
        icon: "error",
        dateTime: "just now",
      });
      openSnackBar();
    }

    setSavingSuspend(false);
  };

  const onSuspendUser = async () => {
    if (!activity) return;
    const isSuspended = await locationSuspendService.checkIsUserLocationSuspended(
      activity.hostId,
      activity.userId
    );

    if (!isSuspended) {
      setSuspendModalOpen(true);
    } else {
      setSnackbar({
        color: "error",
        content: "This user is already suspended or banned.",
        title: "Cannot Suspend",
        icon: "error",
        dateTime: "just now",
      });
      openSnackBar();
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} display="flex" justifyContent="center">
          <Card sx={{ p: 3, textAlign: "center" }}>
            <LoadingSpinner title="Loading visit details…" />
          </Card>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!activity) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} display="flex" justifyContent="center">
          <MDTypography variant="h6" color="error">
            Visit not found.
          </MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const doorLockRequired = activity.doorLockCodeRequired ?? true;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 3 }}>
              {/* HEADER */}
              <MDBox display="flex" alignItems="center" mb={3}>
                <MDBox
                  component="img"
                  src={activity.profiles?.profilePic}
                  alt="User Profile Picture"
                  width="60px"
                  height="60px"
                  borderRadius="50%"
                  mr={2}
                />
                <MDBox>
                  <MDTypography variant="h5" fontWeight="medium">
                    {activity.profiles.firstName} {activity.profiles.lastName}
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    {formatDate(activity.startTime)}
                  </MDTypography>
                </MDBox>
              </MDBox>

              {/* POST-USE PICTURE & RATING */}
              {doorLockRequired ? (
                <>
                  <MDBox mb={3}>
                    <MDTypography variant="h6" fontWeight="medium" mb={1}>
                      After Use Picture
                    </MDTypography>
                    {!pictureLoaded && (
                      <MDTypography variant="button" color="text">
                        Loading picture...
                      </MDTypography>
                    )}
                    <MDBox
                      component="img"
                      src={activity.postUsePicture}
                      onLoad={() => setPictureLoaded(true)}
                      sx={{
                        width: "100%",
                        maxWidth: 400,
                        borderRadius: "12px",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                      }}
                    />
                  </MDBox>

                  <MDBox mb={3}>
                    <MDTypography variant="h6" fontWeight="medium" mb={1}>
                      Rate the User
                    </MDTypography>
                    <MDInput
                      multiline
                      rows={3}
                      value={noteText}
                      onChange={(e: any) => setNoteText(e.target.value)}
                      placeholder="Enter a note..."
                      disabled={activity.UserRating?.[0]?.id !== undefined}
                    />
                    <MDBox mt={1}>
                      <Rating
                        value={userRating}
                        onChange={(_, val) => onRateUser(val || 0)}
                        disabled={activity.UserRating?.[0]?.id !== undefined}
                      />
                    </MDBox>
                  </MDBox>
                </>
              ) : (
                <MDTypography variant="button" color="text">
                  No door lock was required for this visit.
                  <br />
                  No post-use picture is available.
                </MDTypography>
              )}

              {/* ACTION BUTTONS */}
              <MDBox mt={4} display="flex" gap={1}>
                <MDButton variant="outlined" color="secondary" onClick={() => navigate(-1)}>
                  Done
                </MDButton>

                <MDButton color="dark" onClick={() => alert("Report Conditions Modal")}>
                  {activity.PoorConditionReport?.length > 0
                    ? "Report Submitted"
                    : "Report Poor Conditions"}
                </MDButton>
                <MDButton color="dark" onClick={onSuspendUser}>
                 Suspend User
                </MDButton>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Notification */}
        <NotificationAlert
          open={showSnackBar}
          onClose={closeSnackBar}
          close={closeSnackBar}
          color={snackbar.color}
          icon={snackbar.icon}
          title={snackbar.title}
          content={snackbar.content}
          dateTime={snackbar.dateTime}
        />

        {/* SUSPEND USER MODAL */}
        <Dialog
          open={suspendModalOpen}
          onClose={() => setSuspendModalOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {suspendAction === "suspend" ? "Suspend" : "Ban"} {activity.profiles.firstName}
          </DialogTitle>
<DialogContent sx={{ pt: 2, pb: 2, px: 3 }}>
  <FormControl component="fieldset" sx={{ mb: 3 }}>
    <RadioGroup
      row
      value={suspendAction}
      onChange={(e) => setSuspendAction(e.target.value as "suspend" | "ban")}
    >
      <FormControlLabel value="suspend" control={<Radio />} label="Suspend" />
      <FormControlLabel value="ban" control={<Radio />} label="Ban" />
    </RadioGroup>
  </FormControl>

{/* Suspend Length Dropdown */}
{ suspendAction === "suspend" && (
  <FormControl fullWidth sx={{ mb: 3, minHeight: 60 }} error={!!suspendErrors.time}>
    <InputLabel>Suspend Length</InputLabel>
    <Select
      value={suspendLength} // string like "7 days"
      onChange={(e) => setSuspendLength(e.target.value)}
      sx={{ minHeight: 45 }}
    >
      {SUSPEND_MEMBER_TIME.map((item) => (
        <MenuItem key={item.id} value={item.name}>
          {item.name}
        </MenuItem>
      ))}
    </Select>
    <FormHelperText>{suspendErrors.time}</FormHelperText>
  </FormControl>
)}

{/* Reason Dropdown */}
<FormControl fullWidth sx={{ mb: 3, minHeight: 60 }} error={!!suspendErrors.reason}>
  <InputLabel>Reason</InputLabel>
  <Select
    value={suspendReason} // string like "Poor Conditions"
    onChange={(e) => setSuspendReason(e.target.value)}
    sx={{ minHeight: 45 }}
  >
    {SUSPEND_USER_REASONS.map((reason) => (
      <MenuItem key={reason.id} value={reason.name}>
        {reason.name}
      </MenuItem>
    ))}
  </Select>
  <FormHelperText>{suspendErrors.reason}</FormHelperText>
</FormControl>

  <TextField
    label="Notes"
    multiline
    rows={4}
    fullWidth
    value={noteText}
    onChange={(e) => setNoteText(e.target.value)}
    error={!!suspendErrors.note}
    helperText={suspendErrors.note}
    sx={{ mb: 2 }}
  />
</DialogContent>

          <DialogActions>
            <MDButton onClick={() => setSuspendModalOpen(false)} variant="outlined" color="secondary" >
              Cancel
            </MDButton>
            <MDButton onClick={saveSuspend} color="error">
              Save
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default LocationActivityDetailsPage;
